import * as Sentry from "@sentry/vue";
import {captureConsoleIntegration} from "@sentry/integrations";
import Vue from 'vue';
import {fingerprintSeparator} from '@/scripts/logger';

const environments = {
    LOCAL: 'local',
    STAGING: 'staging',
    PRODUCTION: 'production',
}

export default function initSentry() {
    const environment = getSentryEnvironment();
    const dsn = getSentryDataSourceName(environment);
    Sentry.init({
        Vue,
        dsn: dsn,
        environment: environment,
        integrations: [
            captureConsoleIntegration({
                levels: ['error']
            }),
        ],
        beforeBreadcrumb: scrubBreadcrumbHistory,
        beforeSend: filterSentEvents,
    });
};

function scrubBreadcrumbHistory(breadcrumb) {
    // Scrub breadcrumb history for calls before an error occurred
    if (breadcrumb.category === 'xhr' && breadcrumb.data && breadcrumb.data.url) {
        breadcrumb.data.url = queryParamScrubber(breadcrumb.data.url);
    }
    return breadcrumb;
}

function queryParamScrubber(urlString) {
    const url = new URL(urlString);
    url.search = '';
    return url.toString();
}

function filterSentEvents(event, hint) {
    if (event.message) {
        const fingerprintSeparatorIndex = event.message.indexOf(fingerprintSeparator);
        if (fingerprintSeparatorIndex > 0) {
            if (event.logger === 'console') {
                // Do not send console logs containing the fingerprintSeparator,
                // they're already directly sent to Sentry in logger.js.
                return null;
            } else {
                overrideEventFingerprint(event, event.message, fingerprintSeparatorIndex);
            }
        }
    } else if (event.exception) {
        const exception = event.exception.values[0];
        if (exception && exception.value) {
            overrideEventFingerprint(event, exception.value);
        }
    }

    return event;
}

function extractMessageBeforeSeparator(message, separatorIndex) {
    if (separatorIndex > 0) {
        return message.substring(0, separatorIndex);
    }
    return null;
}

function overrideEventFingerprint(event, message, fingerprintSeparatorIndex = null) {
    // Calculate index if not already provided
    if (!fingerprintSeparatorIndex) {
        fingerprintSeparatorIndex = message.indexOf(fingerprintSeparator);
    }

    // If a message has the fingerprintSeparator, group in Sentry by everything before it
    if (fingerprintSeparatorIndex > 0) {
        const fingerprintOverride = extractMessageBeforeSeparator(message, fingerprintSeparatorIndex);
        if (fingerprintOverride) {
            event.fingerprint = [fingerprintOverride];
        }
    }
}

function getSentryEnvironment() {
    let environment = null;

    let environmentProperty = process.env.VUE_APP_SENTRY_ENVIRONMENT || null;
    switch (environmentProperty) {
        case 'local':
            environment = environments.LOCAL;
            break;
        case 'staging':
            environment = environments.STAGING;
            break;
        case 'production':
            environment = environments.PRODUCTION;
            break;
    }

    // Workaround for when VUE_APP_SENTRY_ENVIRONMENT is not set
    if (!environment) {
        const apiBaseUrl = process.env.VUE_APP_SERVICE_URL;
        if (apiBaseUrl) {
            if (apiBaseUrl.includes('https://api.glasshive.com')) {
                environment = environments.PRODUCTION;
            } else if (apiBaseUrl.includes('https://staging.api.glasshive.com')) {
                environment = environments.STAGING;
            }
        }
    }

    return environment;
}

function getSentryDataSourceName(environment) {
    if (environment) {
        return process.env.VUE_APP_SENTRY_DSN;
    }
    return null;
}
